<template>
  <div class="atendimento-360">
    <div
      v-if="loading"
      class="atendimento-loading"
      role="status"
      aria-live="polite"
    >
      <div class="atendimento-loading__spinner"></div>

      <strong>Carregando</strong>

      <span>Aguarde, estamos carregando os dados...</span>
    </div>

    <main v-else class="atendimento-360__container">
      <section class="painel atendimento">
        <header class="atendimento__header">
          <div class="atendimento__topo">
            <h1>
              <i class="icofont-user-alt-7"></i> Atendimento multidimensional
            </h1>

            <div
              class="status-switch"
              role="group"
              aria-label="Filtrar acompanhamentos"
            >
              <button
                type="button"
                class="status-switch__completo"
                :class="{ active: filtroAtendimento === 'completo' }"
                @click="selecionarFiltroAtendimento('completo')"
              >
                <i class="icofont-check-circled"></i> Completo
              </button>
              <button
                type="button"
                class="status-switch__parcial"
                :class="{ active: filtroAtendimento === 'parcial' }"
                @click="selecionarFiltroAtendimento('parcial')"
              >
                <i class="icofont-warning-alt"></i> Parcial
              </button>
            </div>
          </div>

          <div class="pessoa-resumo">
            <div class="pessoa-resumo__cabecalho">
              <div class="pessoa-resumo__nome">
                {{ pessoa.nome ?? '' }}
                <span>| {{ pessoa.sexo ?? '' }}</span>
              </div>
            </div>

            <div class="pessoa-resumo__informacoes">
              <div class="pessoa-resumo__coluna">
                <div class="pessoa-resumo__campo">
                  <span class="pessoa-resumo__rotulo">CPF</span>
                  <span class="pessoa-resumo__valor">
                    {{ formatCPF(pessoa.cpf) ?? '' }}
                  </span>

                  <template v-if="pessoa.cns">
                    <span class="pessoa-resumo__rotulo">CNS</span>
                    <span class="pessoa-resumo__valor">
                      {{ pessoa.cns ?? '' }}
                    </span>
                  </template>
                </div>

                <div class="pessoa-resumo__campo">
                  <span class="pessoa-resumo__rotulo">
                    Data de nascimento
                  </span>

                  <span class="pessoa-resumo__valor">
                    {{ pessoa.nascimento ?? '' }}

                    <template v-if="pessoa.idade">
                      ({{ pessoa.idade }} anos)
                    </template>
                  </span>
                </div>
              </div>

              <div class="pessoa-resumo__coluna">
                <div class="pessoa-resumo__campo">
                  <span class="pessoa-resumo__rotulo"> Nome da mãe </span>

                  <span class="pessoa-resumo__valor">
                    {{ pessoa.nomeMae || '' }}
                  </span>
                </div>

                <div class="pessoa-resumo__campo">
                  <span class="pessoa-resumo__rotulo"> Microárea </span>

                  <span class="pessoa-resumo__valor">
                    {{ pessoa.microArea || '' }}

                    <template v-if="pessoa.profissional">
                      - {{ pessoa.profissional }}
                    </template>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </header>

        <div class="cards-atendimento">
          <button
            v-for="item in acompanhamentosFiltrados"
            :key="item.id"
            type="button"
            class="card-atendimento"
            :class="[
              `card-atendimento--${item.cor}`,
              { 'card-atendimento--ativo': acompanhamentoAtivoId === item.id },
            ]"
            @click="acompanhamentoAtivoId = item.id"
          >
            <i :class="item.icone"></i>
            <span>{{ item.titulo }}</span>
            <span
              class="card-atendimento__status"
              :class="`card-atendimento__status--${item.status}`"
            >
              <i
                :class="
                  item.status === 'completo'
                    ? 'icofont-check'
                    : 'icofont-warning-alt'
                "
              ></i>
            </span>
            <span
              v-if="acompanhamentoAtivoId === item.id"
              class="card-atendimento__seta"
            ></span>
          </button>
        </div>

        <div class="recomendacoes">
          <h2>NESTE ATENDIMENTO</h2>
          <div
            v-if="acompanhamentosFiltrados.length === 0"
            class="recomendacoes__sucesso"
          >
            <i class="icofont-check-circled"></i>
            Nenhuma pendência. Todos os cuidados previstos para esta pessoa
            estão em dia.
          </div>

          <div
            v-else-if="
              acompanhamentoAtivo && acompanhamentoAtivo.status === 'completo'
            "
            :class="[
              'recomendacoes__sucesso',
              { 'recomendacoes__sucesso--alerta': possuiIndicadoresParciais },
            ]"
          >
            <i
              :class="
                possuiIndicadoresParciais
                  ? 'icofont-warning-alt'
                  : 'icofont-check-circled'
              "
            ></i>

            <template v-if="possuiIndicadoresParciais">
              Acesse o filtro <strong>Parcial</strong> acima para ver o que
              precisa ser feito neste atendimento.
            </template>

            <template v-else>
              Nenhuma pendência no indicador. Todos os cuidados previstos para
              esta pessoa estão em dia.
            </template>
          </div>

          <div v-else class="recomendacoes__lista">
            <article
              v-for="(alerta, alertaIndex) in acompanhamentoAtivo.alertas"
              :key="`${acompanhamentoAtivo.id}-${alertaIndex}`"
              class="alerta"
            >
              <div class="alerta__cabecalho">
                <div class="alerta__texto">
                  <i class="icofont-warning-alt"></i>
                  <span>{{ alerta.texto }}</span>
                </div>

                <div
                  v-if="alerta.codigos && !perfilOdontoOuEmulti"
                  class="alerta__acoes"
                >
                  <button
                    v-for="(lista, tipo) in alerta.codigos"
                    :key="tipo"
                    type="button"
                    :class="{ active: painelCodigoAberto(alertaIndex, tipo) }"
                    @click="alternarPainelCodigo(alertaIndex, tipo)"
                  >
                    {{ rotuloTipoCodigo(tipo) }}
                  </button>
                </div>
              </div>

              <div
                v-if="
                  alerta.codigos && codigoAberto.alertaIndex === alertaIndex
                "
                class="codigos"
              >
                <div class="codigos__busca">
                  <i class="icofont-search-1"></i>
                  <input
                    v-model.trim="buscaCodigo"
                    type="text"
                    placeholder="Pesquisar por código ou descrição..."
                  />
                </div>

                <ul>
                  <li
                    v-for="(codigo, index) in codigosFiltrados(alerta)"
                    :key="index"
                  >
                    <div>
                      <strong>{{ codigo.codigo }}</strong>
                      <span>{{ codigo.descricao }}</span>
                    </div>
                    <button
                      type="button"
                      title="Copiar código"
                      @click="copiarCodigo(codigo.codigo)"
                    >
                      <i
                        :class="
                          codigoCopiado === codigo.codigo
                            ? 'icofont-check-circled'
                            : 'icofont-copy'
                        "
                      ></i>
                    </button>
                  </li>
                </ul>
              </div>
            </article>
          </div>
        </div>
      </section>

      <TabelaIndicadores
        :registros="registrosIndicadores"
        @abrir-sigtap="abrirModalSigtap"
        @atualizar-indicadores="indicadores = $event"
      />
    </main>

    <ModalSigtap
      ref="modalSigtap"
      :title="modalSigtap.title"
      :numerador-label="modalSigtap.numeradorLabel"
      :denominador-label="modalSigtap.denominadorLabel"
      :procedimentos-numerador="modalSigtap.procedimentosNumerador"
      :procedimentos-denominador="modalSigtap.procedimentosDenominador"
    />
  </div>
</template>

<script>
import { jwtDecode } from 'jwt-decode'
import utils from '@/mixins/utils'
import { indicadoresConfig } from './constants/index.js'
import { codigosCiaps } from '../utils/ciaps.js'
import { codigosCids } from '../utils/cids.js'
import { codigosSigtap } from '../utils/sigtap.js'
import { getIndicadorSigtapConfig } from './config/indicadoresSigtap.js'
import ModalSigtap from './components/ModalSigtap.vue'
import TabelaIndicadores from './components/TabelaIndicadores.vue'

export default {
  name: 'Atendimento360',
  components: {
    ModalSigtap,
    TabelaIndicadores,
  },
  mixins: [utils],
  data() {
    return {
      loading: true,
      filtroAtendimento: 'parcial',
      acompanhamentoAtivoId: 'saude-mulher',
      buscaCodigo: '',
      codigoCopiado: '',
      codigoAberto: {
        alertaIndex: null,
        tipo: null,
      },
      pessoa: {
        cpf: '',
        cns: '',
        nome: '',
        sexo: '',
        nascimento: '',
        idade: '',
        nomeMae: '',
        profissional: '',
        microArea: '',
        atualizadoEm: '',
        indicadores: {},
        buscaAtiva: {},
        dadosOriginais: {},
      },
      acompanhamentosConfig: {
        b1: {
          id: 'primeira-consulta',
          icone: 'icofont-stethoscope',
        },
        b2: {
          id: 'odontologico',
          icone: 'icofont-tooth',
        },
        b3: {
          id: 'exodontias',
          icone: 'icofont-tooth',
        },
        b4: {
          id: 'escovacao',
          icone: 'icofont-tooth',
        },
        b5: {
          id: 'procedimentos-preventivos',
          icone: 'icofont-tooth',
        },
        b6: {
          id: 'tratamento-atraumatico',
          icone: 'icofont-tooth',
        },
        b7: {
          id: 'gestantes-odontologia',
          icone: 'icofont-businesswoman',
        },
        c1: {
          id: 'mais-acesso',
          icone: 'icofont-doctor',
        },
        c2: {
          id: 'desenvolvimento-infantil',
          icone: 'icofont-baby',
        },
        c3: {
          id: 'gestantes',
          icone: 'icofont-businesswoman',
        },
        c4: {
          id: 'diabetes',
          icone: 'icofont-blood-drop',
        },
        c5: {
          id: 'hipertensao',
          icone: 'icofont-heart-beat',
        },
        c6: {
          id: 'idosa',
          icone: 'icofont-users-alt-5',
        },
        c7: {
          id: 'saude-mulher',
          icone: 'icofont-businesswoman',
        },
      },
      codigosBuscaAtiva: {
        c3a: {
          ciap: ['PARTO', 'ABORTO', 'GRAVIDEZ'],
          cid: ['PARTO', 'ABORTO', 'GRAVIDEZ'],
          sigtap: [
            'HIV',
            'SIFILIS',
            'HEPATITEB',
            'HEPATITEA',
            'GRAVIDEZ',
            'PARTO',
          ],
        },
        c3b: {
          ciap: ['PARTO', 'ABORTO', 'GRAVIDEZ'],
          cid: ['PARTO', 'ABORTO', 'GRAVIDEZ'],
          sigtap: [
            'HIV',
            'SIFILIS',
            'HEPATITEB',
            'HEPATITEA',
            'GRAVIDEZ',
            'PARTO',
          ],
        },
        c3g: {
          ciap: ['PARTO', 'ABORTO', 'GRAVIDEZ'],
          cid: ['PARTO', 'ABORTO', 'GRAVIDEZ'],
          sigtap: ['HIV', 'SIFILIS'],
        },
        c3h: {
          ciap: ['PARTO', 'ABORTO', 'GRAVIDEZ'],
          cid: ['PARTO', 'ABORTO', 'GRAVIDEZ'],
          sigtap: ['HIV', 'SIFILIS'],
        },
        c3i: {
          ciap: ['PARTO', 'ABORTO', 'GRAVIDEZ'],
          cid: ['PARTO', 'ABORTO', 'GRAVIDEZ'],
          sigtap: [
            'HIV',
            'SIFILIS',
            'HEPATITEB',
            'HEPATITEA',
            'GRAVIDEZ',
            'PARTO',
          ],
        },
        c3k: {
          ciap: [],
          cid: [],
          sigtap: [],
        },
        c4a: {
          ciap: ['DIABETES'],
          cid: ['DIABETES'],
          sigtap: [],
        },
        c4b: {
          ciap: ['DIABETES'],
          cid: ['DIABETES'],
          sigtap: ['PA'],
        },
        c4d: {
          ciap: ['DIABETES'],
          cid: ['DIABETES'],
          sigtap: ['ANTROP', 'PESO', 'ALTURA'],
        },
        c4e: {
          ciap: ['DIABETES'],
          cid: ['DIABETES'],
          sigtap: ['DIABETES'],
        },
        c4f: {
          ciap: ['DIABETES'],
          cid: ['DIABETES'],
          sigtap: ['DIABETES'],
        },
        c5a: {
          ciap: ['HIPERTENSAO'],
          cid: ['HIPERTENSAO'],
          sigtap: [],
        },
        c5b: {
          ciap: ['HIPERTENSAO'],
          cid: ['HIPERTENSAO'],
          sigtap: ['PA'],
        },
        c5d: {
          ciap: ['HIPERTENSAO'],
          cid: ['HIPERTENSAO'],
          sigtap: ['ANTROP', 'PESO', 'ALTURA'],
        },
        c7a: {
          ciap: ['SAUDE_SEXUAL_REPRODUTIVA'],
          cid: ['SAUDE_SEXUAL_REPRODUTIVA'],
          sigtap: ['UTERO'],
        },
        c7c: {
          ciap: ['SAUDE_SEXUAL_REPRODUTIVA'],
          cid: ['SAUDE_SEXUAL_REPRODUTIVA'],
          sigtap: ['UTERO', 'MAMA'],
        },
        c7d: {
          ciap: ['SAUDE_SEXUAL_REPRODUTIVA'],
          cid: ['SAUDE_SEXUAL_REPRODUTIVA'],
          sigtap: ['MAMA'],
        },
      },
      indicadores: [],
      registrosIndicadores: [],
      dataFonteIndicadores: '',
      modalSigtap: {
        title: 'SIGTAP',
        numeradorLabel: 'Numerador',
        denominadorLabel: 'Denominador',
        procedimentosNumerador: [],
        procedimentosDenominador: [],
      },
    }
  },
  async mounted() {
    await this.carregarDadosAtendimento()
  },
  computed: {
    acompanhamentosFiltrados() {
      return this.acompanhamentos.filter(
        (item) => item.status === this.filtroAtendimento
      )
    },

    acompanhamentoAtivo() {
      return (
        this.acompanhamentos.find(
          (item) => item.id === this.acompanhamentoAtivoId
        ) || this.acompanhamentosFiltrados[0]
      )
    },

    /**
     * Indica se o profissional pertence a uma equipe de saúde bucal
     * ou eMulti, conforme os indicadores retornados na série histórica.
     *
     * @returns {boolean}
     */
    perfilOdontoOuEmulti() {
      return this.registrosIndicadores.some((registro) =>
        [40, 44].includes(Number(registro?.indicador))
      )
    },

    /**
     * Indica se o profissional pertence a uma equipe de saúde da família
     * ou eMulti, conforme os indicadores retornados na série histórica.
     *
     * @returns {boolean}
     */
    perfilEsfOuEmulti() {
      return this.registrosIndicadores.some((registro) =>
        [49, 44].includes(Number(registro?.indicador))
      )
    },

    /**
     * Indica se o profissional pertence a uma equipe de saúde bucal.
     *
     * @returns {boolean}
     */
    perfilOdontologo() {
      return this.registrosIndicadores.some(
        (registro) => Number(registro?.indicador) === 40
      )
    },

    /**
     * Monta os acompanhamentos do cidadão utilizando as mesmas
     * regras do componente MaisIndicadores.
     *
     * O layout e a configuração visual permanecem próprios
     * do Atendimento360.
     *
     * @returns {Array}
     */
    acompanhamentos() {
      const indicadores = this.pessoa.indicadores || {}
      const buscaAtiva = this.pessoa.buscaAtiva || {}

      return Object.keys(indicadores)
        .sort()
        .map((indicadorKey) => {
          const regraIndicador = indicadoresConfig[indicadorKey]
          const visual = this.acompanhamentosConfig[indicadorKey]

          if (!regraIndicador || !visual) {
            return null
          }

          const alertas = this.montarAlertasIndicador(
            indicadorKey,
            regraIndicador,
            buscaAtiva
          )

          const classificacao = this.getClassificacaoIndicador(indicadorKey)

          return {
            id: visual.id,
            indicador: indicadorKey,
            titulo: regraIndicador.label,
            icone: visual.icone,
            classificacao,
            cor: this.getCorIndicador(classificacao),
            percentual: Number(indicadores[indicadorKey] || 0),
            status: alertas.length > 0 ? 'parcial' : 'completo',
            alertas,
          }
        })
        .filter(Boolean)
    },

    fontesCodigosClinicos() {
      return {
        ciap: codigosCiaps,
        cid: codigosCids,
        sigtap: codigosSigtap,
      }
    },

    possuiIndicadoresParciais() {
      return this.acompanhamentos.some((item) => item.status === 'parcial')
    },
  },

  methods: {
    /**
     * Retorna a classificação do indicador.
     *
     * @param {string} indicadorKey
     * @returns {string}
     */
    getClassificacaoIndicador(indicadorKey) {
      const mapa = {
        c1: 46,
        c2: 51,
        c3: 52,
        c4: 48,
        c5: 50,
        c6: 49,
        c7: 47,
        b1: 38,
        b2: 43,
        b3: 39,
        b4: 42,
        b5: 40,
        b6: 41,
        m1: 44,
        m2: 45,
      }

      const indicador = this.indicadores.find(
        (item) => item.id === mapa[indicadorKey]
      )

      return indicador?.classificacao || 'Regular'
    },
    /**
     * Retorna a cor visual do acompanhamento conforme
     * a classificação do indicador.
     *
     * @param {string} classificacao
     * @returns {string}
     */
    getCorIndicador(classificacao) {
      switch (this.normalizar(classificacao)) {
        case 'otimo':
          return 'otima'

        case 'bom':
          return 'boa'

        case 'suficiente':
          return 'suficiente'

        default:
          return 'regular'
      }
    },

    /**
     * Monta os alertas pendentes de um indicador.
     *
     * Uma busca ativa é considerada pendente quando seu valor é igual a zero.
     *
     * @param {string} indicadorKey
     * @param {Object} indicadorConfig
     * @param {Object} buscaAtiva
     * @returns {Array}
     */
    montarAlertasIndicador(indicadorKey, indicadorConfig, buscaAtiva) {
      const definicoes = indicadorConfig.buscaAtiva || {}

      return Object.keys(buscaAtiva)
        .filter((key) => key.startsWith(indicadorKey))
        .sort()
        .map((key) => {
          const sufixo = key.replace(indicadorKey, '')
          const definicao = definicoes[sufixo]

          return {
            chave: key,
            valor: Number(buscaAtiva[key]),
            texto: this.getTextoBuscaAtiva(definicao, key),
            codigos: this.getCodigosBuscaAtiva(key),
          }
        })
        .filter((item) => item.valor === 0)
    },

    /**
     * Retorna o texto configurado para um item de busca ativa.
     *
     * @param {string|Function|undefined} definicao
     * @param {string} key
     * @returns {string}
     */
    getTextoBuscaAtiva(definicao, key) {
      if (typeof definicao === 'function') {
        return definicao({
          cidadao: this.pessoa.dadosOriginais,
          formatDate: this.formatDate,
          perfilOdontoOuEmulti: this.perfilOdontoOuEmulti,
          perfilEsfOuEmulti: this.perfilEsfOuEmulti,
          perfilOdontologo: this.perfilOdontologo,
        })
      }

      if (typeof definicao === 'string') {
        return definicao
      }

      return `Item ${key}`
    },

    /**
     * Retorna os códigos clínicos associados a uma busca ativa,
     * filtrando as fontes pelo campo id_grupo.
     *
     * @param {string} key
     * @returns {Object|null}
     */
    getCodigosBuscaAtiva(key) {
      const configuracao = this.codigosBuscaAtiva[key]

      if (!configuracao) {
        return null
      }

      const codigos = Object.entries(configuracao).reduce(
        (resultado, [tipo, grupos]) => {
          const fonte = this.fontesCodigosClinicos[tipo] || []

          const itens = this.filtrarCodigosPorGrupo(fonte, grupos)

          if (itens.length > 0) {
            resultado[tipo] = itens
          }

          return resultado
        },
        {}
      )

      return Object.keys(codigos).length > 0 ? codigos : null
    },

    /**
     * Filtra uma lista de códigos pelos grupos configurados.
     *
     * @param {Array} fonte
     * @param {Array} grupos
     * @returns {Array}
     */
    filtrarCodigosPorGrupo(fonte, grupos) {
      if (!Array.isArray(fonte) || !Array.isArray(grupos)) {
        return []
      }

      const gruposNormalizados = grupos.map((grupo) =>
        this.normalizarGrupoCodigo(grupo)
      )

      return fonte
        .filter((item) =>
          gruposNormalizados.includes(
            this.normalizarGrupoCodigo(item?.id_grupo)
          )
        )
        .map((item) => ({
          codigo: String(item?.codigo || ''),
          descricao: String(item?.nome || item?.descricao || ''),
          grupo: String(item?.id_grupo || ''),
        }))
        .filter((item) => item.codigo)
    },

    /**
     * Normaliza o identificador de grupo utilizado pelos arquivos de códigos.
     *
     * @param {string} grupo
     * @returns {string}
     */
    normalizarGrupoCodigo(grupo) {
      return String(grupo || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toUpperCase()
        .trim()
    },

    /**
     * Carrega os dados necessários para montar a página de atendimento 360.
     */
    async carregarDadosAtendimento() {
      this.loading = true

      const token = this.$store.getters['pep/token']

      if (!token) {
        console.error('Token de autenticação do PEP não encontrado.')
        return
      }

      try {
        const dadosToken = jwtDecode(token)

        const coIbge = String(dadosToken.ibge || '').trim()

        const documentoProfissional = String(
          dadosToken.documento_profissional || ''
        ).trim()

        const documentoCidadao = String(
          dadosToken.documento_cidadao || ''
        ).trim()

        if (!coIbge) {
          throw new Error('Código IBGE não encontrado no token do PEP.')
        }

        await this.buscarCidadao(coIbge, documentoCidadao)

        const profissional = await this.buscarProfissionalSaude(
          coIbge,
          documentoProfissional
        )

        const nuIne = String(profissional?.nu_ine || '').trim()

        if (!nuIne) {
          console.warn('O profissional de saúde não possui INE informado.')
          return
        }

        await this.buscarSerieHistoricaEquipe(coIbge, nuIne)
      } catch (error) {
        console.error('Erro ao carregar os dados do atendimento 360:', error)
      } finally {
        this.loading = false
      }
    },

    /**
     * Busca os dados do cidadão informado pelo PEC.
     *
     * @param {string} coIbge
     * @param {string} documentoCidadao
     * @returns {Promise<Object>}
     */
    async buscarCidadao(coIbge, documentoCidadao) {
      const payload = this.montarPayloadDocumentoCidadao(documentoCidadao)

      const { data } = await this.$apiPep.post(
        `/pep/app/cidadao/${coIbge}`,
        payload
      )

      this.pessoa = this.mapearDadosCidadao(data)

      await this.$nextTick()

      this.selecionarPrimeiroAcompanhamento()

      return data
    },

    /**
     * Seleciona inicialmente o primeiro acompanhamento parcial.
     *
     * Caso não existam pendências, seleciona o primeiro acompanhamento completo.
     *
     * @returns {void}
     */
    selecionarPrimeiroAcompanhamento() {
      const primeiroParcial = this.acompanhamentos.find(
        (item) => item.status === 'parcial'
      )

      const primeiro = primeiroParcial || this.acompanhamentos[0] || null

      if (!primeiro) {
        this.acompanhamentoAtivoId = null
        return
      }

      this.filtroAtendimento = primeiro.status
      this.acompanhamentoAtivoId = primeiro.id
    },

    /**
     * Converte o retorno da API para a estrutura utilizada pela interface.
     *
     * @param {Object} cidadao
     * @returns {Object}
     */
    mapearDadosCidadao(cidadao) {
      return {
        cpf: String(cidadao?.nu_cpf || ''),
        cns: String(cidadao?.nu_cns || ''),
        nome: String(cidadao?.no_cidadao || ''),
        sexo: this.formatarTexto(cidadao?.sexo),
        nascimento: this.formatarData(cidadao?.dt_nascimento),
        idade: Number(cidadao?.idade || 0),
        nomeMae: String(cidadao?.no_mae || ''),
        profissional: String(cidadao?.no_profissional || ''),
        microArea: String(cidadao?.micro_area || ''),
        atualizadoEm: this.formatarDataHora(cidadao?.dt_atualizado),
        indicadores: this.parseObjectJson(cidadao?.indicadores),
        buscaAtiva: this.parseObjectJson(cidadao?.busca_ativa),
        dadosOriginais: cidadao || {},
      }
    },

    /**
     * Formata uma data no padrão AAAA-MM-DD para DD/MM/AAAA.
     *
     * @param {string} data
     * @returns {string}
     */
    formatarData(data) {
      const valor = String(data || '').trim()

      if (!valor) {
        return '-'
      }

      const apenasData = valor.split(' ')[0]
      const partes = apenasData.split('-')

      if (partes.length !== 3) {
        return valor
      }

      const [ano, mes, dia] = partes

      return `${dia}/${mes}/${ano}`
    },

    /**
     * Formata uma data e hora retornada pela API.
     *
     * @param {string} dataHora
     * @returns {string}
     */
    formatarDataHora(dataHora) {
      const valor = String(dataHora || '').trim()

      if (!valor) {
        return '-'
      }

      const [data, hora = ''] = valor.split(' ')
      const dataFormatada = this.formatarData(data)

      return hora ? `${dataFormatada} ${hora}` : dataFormatada
    },

    /**
     * Formata um texto deixando apenas a primeira letra maiúscula.
     *
     * @param {string} texto
     * @returns {string}
     */
    formatarTexto(texto) {
      const valor = String(texto || '')
        .trim()
        .toLowerCase()

      if (!valor) {
        return '-'
      }

      return valor.charAt(0).toUpperCase() + valor.slice(1)
    },

    /**
     * Busca os dados do profissional de saúde informado pelo PEC.
     *
     * @param {string} coIbge
     * @param {string} documentoProfissional
     * @returns {Promise<Object>}
     */
    async buscarProfissionalSaude(coIbge, documentoProfissional) {
      const { data } = await this.$apiPep.post(
        `/pep/app/profissional-saude/${coIbge}`,
        {
          nu_cpf: documentoProfissional,
        }
      )

      return data
    },

    /**
     * Busca a série histórica da equipe do profissional autenticado.
     *
     * @param {string} coIbge
     * @param {string} nuIne
     * @returns {Promise<Array>}
     */
    async buscarSerieHistoricaEquipe(coIbge, nuIne) {
      const { data } = await this.$apiPep.get(
        `/pep/app/serie-historica/${coIbge}/equipe/${nuIne}`
      )

      this.registrosIndicadores = Array.isArray(data) ? data : []

      return data
    },

    /**
     * Monta o corpo da busca do cidadão conforme o documento recebido.
     *
     * @param {string} documentoCidadao
     * @returns {{nu_cpf?: string, nu_cns?: string}}
     */
    montarPayloadDocumentoCidadao(documentoCidadao) {
      const documento = String(documentoCidadao || '').replace(/\D/g, '')

      if (documento.length === 11) {
        return {
          nu_cpf: documento,
        }
      }

      if (documento.length === 15) {
        return {
          nu_cns: documento,
        }
      }

      throw new Error(
        'O documento do cidadão informado pelo PEC não é um CPF ou CNS válido.'
      )
    },

    selecionarFiltroAtendimento(status) {
      this.filtroAtendimento = status
      this.codigoAberto = { alertaIndex: null, tipo: null }
      this.buscaCodigo = ''

      const primeiro = this.acompanhamentos.find(
        (item) => item.status === status
      )

      this.acompanhamentoAtivoId = primeiro ? primeiro.id : null
    },

    alternarPainelCodigo(alertaIndex, tipo) {
      const jaAberto = this.painelCodigoAberto(alertaIndex, tipo)

      this.codigoAberto = jaAberto
        ? { alertaIndex: null, tipo: null }
        : { alertaIndex, tipo }

      this.buscaCodigo = ''
    },

    painelCodigoAberto(alertaIndex, tipo) {
      return (
        this.codigoAberto.alertaIndex === alertaIndex &&
        this.codigoAberto.tipo === tipo
      )
    },

    codigosFiltrados(alerta) {
      const lista = alerta.codigos?.[this.codigoAberto.tipo] || []
      const termo = this.normalizar(this.buscaCodigo)

      if (!termo) return lista

      return lista.filter((item) =>
        this.normalizar(`${item.codigo} ${item.descricao}`).includes(termo)
      )
    },

    rotuloTipoCodigo(tipo) {
      return (
        {
          ciap: 'CIAP-2',
          cid: 'CID-10',
          sigtap: 'SIGTAP',
        }[tipo] || tipo.toUpperCase()
      )
    },

    async copiarCodigo(codigo) {
      try {
        await navigator.clipboard.writeText(codigo)
      } catch (error) {
        const input = document.createElement('textarea')
        input.value = codigo
        document.body.appendChild(input)
        input.select()
        document.execCommand('copy')
        document.body.removeChild(input)
      }

      this.codigoCopiado = codigo
      window.setTimeout(() => {
        this.codigoCopiado = ''
      }, 1500)
    },

    normalizar(valor) {
      return String(valor || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim()
    },

    /**
     * Preenche e abre o modal de procedimentos SIGTAP.
     *
     * Atualmente apenas os indicadores 39, 40 e 41 possuem suporte.
     *
     * @param {Object} indicador
     * @returns {void}
     */
    abrirModalSigtap(indicador) {
      const indicadoresHabilitados = [39, 40, 41]

      if (!indicadoresHabilitados.includes(Number(indicador?.id))) {
        return
      }

      const configuracao = getIndicadorSigtapConfig(indicador.id)

      if (!configuracao) {
        return
      }

      this.modalSigtap = {
        title: configuracao.title || indicador.nome || 'SIGTAP',

        numeradorLabel: configuracao.numerador?.label || 'Numerador',

        denominadorLabel: configuracao.denominador?.label || 'Denominador',

        procedimentosNumerador: Array.isArray(
          configuracao.numerador?.procedimentos
        )
          ? configuracao.numerador.procedimentos
          : [],

        procedimentosDenominador: Array.isArray(
          configuracao.denominador?.procedimentos
        )
          ? configuracao.denominador.procedimentos
          : [],
      }

      this.$nextTick(() => {
        this.$refs.modalSigtap?.open()
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.atendimento-360 {
  min-height: 100vh;
  padding: 2rem;
  color: #1f2937;
  background: #f8fafc;

  &__container {
    width: 100%;
    max-width: 1280px;
    margin: 0 auto;
  }
}

.painel {
  padding: 2rem;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 1rem;
  box-shadow: 0 4px 18px rgba(15, 23, 42, 0.06);
}

.atendimento {
  &__header {
    padding-bottom: 1.25rem;
  }

  &__topo {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;

    h1 {
      display: flex;
      align-items: center;
      gap: 0.6rem;
      margin: 0;
      font-size: 1.55rem;
      font-weight: 700;
      color: #111827;

      i {
        color: #3b82f6;
      }
    }
  }
}

.status-switch,
.segmentado {
  display: inline-flex;
  padding: 0.25rem;
  background: #e5e7eb;
  border-radius: 999px;

  button {
    min-width: 120px;
    padding: 0.5rem 0.9rem;
    color: #6b7280;
    font-size: 0.78rem;
    font-weight: 700;
    background: transparent;
    border: 0;
    border-radius: 999px;
    transition: 0.2s;

    &.active {
      background: #fff;
      box-shadow: 0 1px 4px rgba(15, 23, 42, 0.15);
    }
  }

  &__completo {
    color: #15803d;

    &:hover,
    &.active {
      color: #15803d;
    }
  }

  &__parcial {
    color: #dc2626;

    &:hover,
    &.active {
      color: #dc2626;
    }
  }
}

.pessoa-resumo {
  padding: 1rem 1.25rem;
  margin-top: 1rem;
  background: #fff;
  border: 1px solid #e6ebf2;
  border-radius: 10px;

  &__cabecalho {
    margin-bottom: 5px;
  }

  &__nome {
    font-size: 16px;
    font-weight: 700;
    color: #111827;

    span {
      color: #000;
      font-weight: 500;
      text-transform: uppercase;
      font-size: 14px;
    }
  }

  &__informacoes {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem 2rem;
  }

  &__coluna {
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
  }

  &__campo {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.35rem;
  }

  &__rotulo {
    font-size: 16px;
    font-weight: 700;
    color: #000;
  }

  &__valor {
    color: #111827;
    font-size: 14px;
  }
}

@media (max-width: 768px) {
  .pessoa-resumo {
    &__informacoes {
      grid-template-columns: 1fr;
    }
  }
}

.cards-atendimento {
  display: flex;
  flex-wrap: wrap;
  gap: 0.7rem;
  margin-bottom: 2rem;
}

.card-atendimento {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.7rem 1rem;
  font-size: 0.86rem;
  font-weight: 600;
  color: #374151;
  background: #fff;
  border: 2px solid #dbeafe;
  border-radius: 0.75rem;
  transition: 0.2s;

  > i {
    font-size: 1.25rem;
  }
  &:hover,
  &--ativo {
    background: #f8fafc;
    box-shadow: 0 2px 7px rgba(15, 23, 42, 0.08);
  }

  &--otima {
    border-color: #1212b6;

    > i {
      color: #1212b6;
    }

    .card-atendimento__seta {
      border-top-color: #1212b6;
    }
  }

  &--boa {
    border-color: #38ba38;

    > i {
      color: #38ba38;
    }

    .card-atendimento__seta {
      border-top-color: #38ba38;
    }
  }

  &--suficiente {
    border-color: #d5ba0a;

    > i {
      color: #d5ba0a;
    }

    .card-atendimento__seta {
      border-top-color: #d5ba0a;
    }
  }

  &--regular {
    border-color: #c46500;

    > i {
      color: #c46500;
    }

    .card-atendimento__seta {
      border-top-color: #c46500;
    }
  }

  &__status {
    position: absolute;
    top: -0.45rem;
    right: -0.45rem;
    display: grid;
    width: 1.35rem;
    height: 1.35rem;
    place-items: center;
    font-size: 0.7rem;
    border: 2px solid #fff;
    border-radius: 50%;

    &--parcial {
      color: #dc2626;
      background: #fee2e2;
    }
    &--completo {
      color: #16a34a;
      background: #dcfce7;
    }
  }

  &__seta {
    position: absolute;
    bottom: -0.55rem;
    left: 50%;
    width: 0;
    height: 0;
    border-top: 0.55rem solid #cbd5e1;
    border-right: 0.55rem solid transparent;
    border-left: 0.55rem solid transparent;
    transform: translateX(-50%);
  }
}

.recomendacoes {
  padding: 1.25rem;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 0.8rem;

  h2 {
    margin: 0 0 1rem;
    font-size: 0.78rem;
    font-weight: 800;
    color: #475569;
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }

  &__lista {
    display: grid;
    gap: 1rem;
  }

  &__sucesso {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    padding: 1.25rem;
    color: #15803d;
    font-size: 0.9rem;
    font-weight: 600;
    background: #f0fdf4;
    border: 1px solid #bbf7d0;
    border-radius: 0.75rem;
    &--alerta {
      color: #991b1b;
      background: #fef2f2;
      border-color: #fecaca;

      i {
        color: #dc2626;
      }
    }
  }
}

.alerta {
  overflow: hidden;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 0.75rem;

  &__cabecalho {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 1rem;
  }

  &__texto {
    display: flex;
    align-items: flex-start;
    gap: 0.7rem;
    color: #991b1b;
    font-size: 0.86rem;
    font-weight: 600;
    line-height: 1.45;

    i {
      margin-top: 0.15rem;
      color: #dc2626;
      font-size: 1.15rem;
    }
  }

  &__acoes {
    display: flex;
    flex-wrap: wrap;
    gap: 0.45rem;

    button {
      padding: 0.4rem 0.65rem;
      color: #4b5563;
      font-size: 0.72rem;
      font-weight: 700;
      background: #fff;
      border: 1px solid #d1d5db;
      border-radius: 0.4rem;

      &.active {
        color: #1d4ed8;
        background: #eff6ff;
        border-color: #60a5fa;
      }
    }
  }
}

.codigos {
  padding: 1rem;
  background: #fff;
  border-top: 1px solid #fee2e2;

  &__busca {
    position: relative;
    max-width: 420px;
    margin-bottom: 0.75rem;

    i {
      position: absolute;
      top: 50%;
      left: 0.85rem;
      color: #9ca3af;
      transform: translateY(-50%);
    }
    input {
      width: 100%;
      padding: 0.65rem 0.8rem 0.65rem 2.35rem;
      border: 1px solid #d1d5db;
      border-radius: 0.55rem;
      outline: 0;
    }
  }

  ul {
    display: grid;
    gap: 0.5rem;
    max-height: 230px;
    padding: 0;
    margin: 0;
    overflow-y: auto;
    list-style: none;
  }

  li {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 0.7rem;
    background: #f8fafc;
    border: 1px solid #e5e7eb;
    border-radius: 0.55rem;

    div {
      display: flex;
      align-items: center;
      gap: 0.8rem;
    }
    strong {
      padding: 0.25rem 0.45rem;
      font-family: monospace;
      background: #fff;
      border: 1px solid #d1d5db;
      border-radius: 0.3rem;
    }
    span {
      font-size: 0.82rem;
      color: #4b5563;
    }
    button {
      color: #64748b;
      background: transparent;
      border: 0;
      font-size: 1.1rem;
    }
  }
}

.atendimento-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 0.75rem;
  min-height: calc(100vh - 4rem);
  padding: 2rem;
  text-align: center;

  strong {
    color: #1f2937;
    font-size: 1.1rem;
    font-weight: 700;
  }

  span {
    max-width: 420px;
    color: #64748b;
    font-size: 0.9rem;
    line-height: 1.5;
  }

  &__spinner {
    width: 3rem;
    height: 3rem;
    border: 0.25rem solid #dbeafe;
    border-top-color: #3b82f6;
    border-radius: 50%;
    animation: atendimento-loading-spin 0.8s linear infinite;
  }
}

@keyframes atendimento-loading-spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 768px) {
  .atendimento-360 {
    padding: 1rem;
  }

  .painel {
    padding: 1rem;
  }

  .atendimento__topo,
  .alerta__cabecalho {
    align-items: stretch;
    flex-direction: column;
  }

  .status-switch {
    width: 100%;
  }

  .status-switch button {
    flex: 1;
    min-width: 0;
  }

  .card-atendimento {
    width: 100%;
  }

  .codigos li div {
    align-items: flex-start;
    flex-direction: column;
    gap: 0.3rem;
  }
}
</style>
